import { Icon, Tooltip } from '@analog/ui';
import allImgPaths from 'assets/allImgPaths';
import { Text, TruncatedText } from 'components';
import Badge from 'components/common/Badge';
import JsonTable from 'components/common/JsonTable';
import { Skeleton } from 'components/Skeleton';
import TableColumns from 'components/Table/TableColumns';
import { FontColors } from 'consts';
import moment from 'moment';
import { tooltipOfPhase } from 'pages/GMP/GMPList/components/GMPMessageColumns';
import { routes } from 'routes/routesConst';
import
  {
    classes,
    colors,
    colorType,
    DBtoStringValueMapping,
    getIcon,
    getLinkFromNetwork,
    getNetworkName,
    parseNetwork,
    timeAgo,
  } from 'utils';

import { TaskDataProps, useTaskData } from './useTaskData';

const ReadTaskData = ({ taskDetailsData }: TaskDataProps) => {
  const { navigate } = useTaskData({ taskDetailsData });
  const destNetwork = getNetworkName(taskDetailsData?.taskDetails?.network as string);

  return [
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Task ID',
      value: (
        <Skeleton.Loader>
          <div className="text-white">{taskDetailsData?.taskDetails.taskId}</div>
        </Skeleton.Loader>
      ),
      enable: !!taskDetailsData?.taskDetails.taskId || false,
    },
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Received batch Task ID',
      value: (
        <Skeleton.Loader>
          <Text
            color={FontColors.PURPLE}
            extraClass="cursor-pointer"
            onClick={() =>
              navigate(
                `${routes.submitGateway}/${taskDetailsData?.taskDetails?.receivedSubmitTaskId}`,
                { state: { isTaskId: true } }
              )
            }
          >
            {taskDetailsData?.taskDetails?.receivedSubmitTaskId}
          </Text>
        </Skeleton.Loader>
      ),
      enable: !!taskDetailsData?.taskDetails?.receivedSubmitTaskId || false,
    },
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Executed batch Task ID',
      value: (
        <Skeleton.Loader>
          <Text
            color={FontColors.PURPLE}
            extraClass="cursor-pointer"
            onClick={() =>
              navigate(
                `${routes.submitGateway}/${taskDetailsData?.taskDetails?.executedSubmitTaskId}`,
                { state: { isTaskId: true } }
              )
            }
          >
            {taskDetailsData?.taskDetails?.executedSubmitTaskId}
          </Text>
        </Skeleton.Loader>
      ),
      enable: !!taskDetailsData?.taskDetails?.executedSubmitTaskId || false,
    },
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Received Message IDs',
      value: (
        <Skeleton.Loader>
          <div className="flex flex-col gap-2 w-full">
            {taskDetailsData?.taskDetails?.receivedMessageIds.length &&
              taskDetailsData?.taskDetails?.receivedMessageIds.map((receivedMessageId) => (
                <TruncatedText
                  originalText={receivedMessageId}
                  textColor={FontColors.PURPLE}
                  withCopyButton
                  dataToolTipId={receivedMessageId}
                  className="cursor-pointer"
          toolTipClass="!cursor-text"
                  dataToolTipContent={receivedMessageId}
                  onClick={() =>
                    navigate(
                      `${routes.message}/${receivedMessageId}`,
                    )
                  }
                />
              ))}
          </div>
        </Skeleton.Loader>
      ),
      enable: !!taskDetailsData?.taskDetails?.receivedMessageIds.length || false,
    },
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Executed Message IDs',
      value: (
        <Skeleton.Loader>
          <div className="flex flex-col gap-2 w-full">
            {taskDetailsData?.taskDetails?.executedMessageIds.length &&
              taskDetailsData?.taskDetails?.executedMessageIds.map((executedMessageId) => (
                <TruncatedText
                  originalText={executedMessageId}
                  textColor={FontColors.PURPLE}
                  withCopyButton
                  dataToolTipId={executedMessageId}
                  className="cursor-pointer"
          toolTipClass="!cursor-text"
                  dataToolTipContent={executedMessageId}
                  onClick={() =>
                    navigate(
                      `${routes.message}/${executedMessageId}`,
                    )
                  }
                />
              ))}
          </div>
        </Skeleton.Loader>
      ),
      enable: !!taskDetailsData?.taskDetails?.executedMessageIds.length || false,
    },
    {
      icon: <Icon icon={allImgPaths.phaseIcon} />,
      label: 'Phase',
      value: (
        <Skeleton.Loader className="w-20 h-5">
          <Tooltip
            id={tooltipOfPhase[taskDetailsData?.taskDetails.phase as keyof typeof tooltipOfPhase]}
            content={
              tooltipOfPhase[taskDetailsData?.taskDetails.phase as keyof typeof tooltipOfPhase]
            }
            className="max-w-[220px] md:!max-w-fit !rounded-lg z-10"
            clickable
          />
          <Badge
            label={
              taskDetailsData?.taskDetails.phase
                ? DBtoStringValueMapping[
                    taskDetailsData?.taskDetails.phase.toLowerCase() as keyof typeof DBtoStringValueMapping
                  ] || taskDetailsData?.taskDetails.phase
                : ''
            }
            badgeColor={
              taskDetailsData?.taskDetails.phase &&
              taskDetailsData?.taskDetails.phase.toLowerCase() in colors
                ? (colors[
                    taskDetailsData?.taskDetails.phase.toLowerCase() as keyof typeof colors
                  ] as colorType)
                : 'red'
            }
            data-tooltip-id={
              tooltipOfPhase[taskDetailsData?.taskDetails.phase as keyof typeof tooltipOfPhase]
            }
          />
        </Skeleton.Loader>
      ),
      enable: !!taskDetailsData?.taskDetails.phase || false,
    },
    {
      icon: <Icon icon={allImgPaths.mathIcon} />,
      label: 'Action',
      value: (
        <Skeleton.Loader className="w-20 h-5">
          <div className="flex flex-col gap-2 sm:flex-row">
            {taskDetailsData?.taskDetails?.hasReceived && (
              <Badge label={'RECEIVED'} badgeColor="blue" />
            )}
            {taskDetailsData?.taskDetails?.hasExecuted && (
              <Badge label={'EXECUTED'} badgeColor="brightGreen" />
            )}
          </div>
        </Skeleton.Loader>
      ),
      enable:
        !!taskDetailsData?.taskDetails?.hasReceived ||
        !!taskDetailsData?.taskDetails?.hasExecuted ||
        false,
    },
    {
      icon: <Icon icon={allImgPaths.sourceNetworkIcon} />,
      label: 'Network',
      value: (
        <div className="flex gap-1 text-white">
          {taskDetailsData?.taskDetails.network ? (
            <Skeleton.Loader>
              <div className={classes('flex gap-1 flex-wrap')}>
                <TableColumns
                  key={taskDetailsData?.taskDetails.network}
                  beforeImagePath={destNetwork && getIcon(destNetwork)}
                  value={parseNetwork(taskDetailsData?.taskDetails.network)}
                  textColor={FontColors.WHITE}
                  classNames={{
                    container: '!w-fit',
                    base: '!w-fit',
                    content: 'capitalize',
                    skeletonText: 'h-4 w-12',
                  }}
                />
              </div>
            </Skeleton.Loader>
          ) : (
            <Skeleton.Loader className="h-4 w-10">-</Skeleton.Loader>
          )}
        </div>
      ),
      enable: !!taskDetailsData?.taskDetails.network,
    },
    {
      icon: <Icon icon={allImgPaths.clockIcon} />,
      label: 'Created At',
      value: taskDetailsData?.taskDetails?.created_at
        ? `${timeAgo(new Date(taskDetailsData?.taskDetails?.created_at))} (${moment
            .utc(taskDetailsData?.taskDetails?.created_at)
            .format('MMM-D-YYYY h:mm:ss A +UTC')})`
        : '-',
      enable: !!taskDetailsData?.taskDetails.created_at,
    },
    {
      icon: <Icon icon={allImgPaths.clockIcon} />,
      label: 'Updated At',
      value: taskDetailsData?.taskDetails?.updated_at
        ? `${timeAgo(new Date(taskDetailsData?.taskDetails?.updated_at))} (${moment
            .utc(taskDetailsData?.taskDetails?.updated_at)
            .format('MMM-D-YYYY h:mm:ss A +UTC')})`
        : '-',
      enable: !!taskDetailsData?.taskDetails.updated_at,
    },
    {
      icon: <Icon icon={allImgPaths.transferIcon} />,
      label: 'Proposed At Block',
      value: (
        <Text
          extraClass="cursor-pointer"
          color={FontColors.PURPLE}
          onClick={() =>
            taskDetailsData?.taskDetails?.proposed_at_block &&
            navigate(`${routes.block}/${taskDetailsData?.taskDetails?.proposed_at_block}`)
          }
        >
          {taskDetailsData?.taskDetails?.proposed_at_block}
        </Text>
      ),
      enable: !!taskDetailsData?.taskDetails.proposed_at_block,
    },
    {
      icon: <Icon icon={allImgPaths.clipboardTickIcon} />,
      label: 'Modified At Block',
      value: (
        <Text
          extraClass="cursor-pointer"
          color={FontColors.PURPLE}
          onClick={() =>
            taskDetailsData?.taskDetails?.modified_at_block &&
            navigate(`${routes.block}/${taskDetailsData?.taskDetails?.modified_at_block}`)
          }
        >
          {taskDetailsData?.taskDetails?.modified_at_block}
        </Text>
      ),
      enable: !!taskDetailsData?.taskDetails.modified_at_block,
    },
    {
      icon: <Icon icon={allImgPaths.clipboardTickIcon} />,
      label: 'Start Block',
      value: (
        <Text
          extraClass="cursor-pointer"
          color={FontColors.PURPLE}
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `block/${taskDetailsData?.taskDetails.definition.readGatewayEvents.blocks[0]}`,
              getNetworkName(taskDetailsData?.taskDetails?.network || '')
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
        >
          {taskDetailsData?.taskDetails?.definition.readGatewayEvents.blocks[0]}
        </Text>
      ),
      enable: !!taskDetailsData?.taskDetails.definition.readGatewayEvents.blocks[0],
    },
    {
      icon: <Icon icon={allImgPaths.clipboardTickIcon} />,
      label: 'End Block',
      value: (
        <Text
          extraClass="cursor-pointer"
          color={FontColors.PURPLE}
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `block/${taskDetailsData?.taskDetails.definition.readGatewayEvents.blocks[1]}`,
              getNetworkName(taskDetailsData?.taskDetails?.network || '')
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
        >
          {taskDetailsData?.taskDetails?.definition.readGatewayEvents.blocks[1]}
        </Text>
      ),
      enable: !!taskDetailsData?.taskDetails.definition.readGatewayEvents.blocks[1],
    },
    {
      icon: <Icon icon={allImgPaths.infoIcon} />,
      label: 'JSON Definition',
      collapsible: true,
      value: (
        <div>
          {taskDetailsData?.taskDetails.definition && (
            <JsonTable
              index={taskDetailsData?.taskDetails.definition.toString() as string}
              jsonData={taskDetailsData?.taskDetails.definition}
            />
          )}
        </div>
      ),
      enable: !!taskDetailsData?.taskDetails.definition,
    },
  ];
};
export default ReadTaskData;
